import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"

export default function PrivacyPolicy() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Fusegu Security</title>
        <meta 
          name="description" 
          content="How Fusegu collects, uses, and protects your data. Our commitment to user privacy and security. Read our privacy policy and data practices." 
        />
        <meta property="og:title" content="Privacy Policy | Fusegu" />
        <meta property="og:description" content="Learn about Fusegu's data collection, usage, and protection practices." />
        <meta property="og:url" content="https://fusegusecurity.com/privacy" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
        <div className="flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <FuseguLogo className="h-6 w-6 flex-shrink-0" />
            <span className="text-lg font-bold">Fusegu</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Privacy Policy
              </span>
            </h1>
            <p className="text-muted-foreground text-lg">Last updated: October 2025</p>
          </div>

          <div className="space-y-6">
            {/* Introduction */}
            <Card className="border-2 border-blue-100 dark:border-blue-900 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Introduction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  Fusegu Security ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our security scanning platform.
                </p>
                <p>
                  By using our service, you agree to the collection and use of information in accordance with this policy.
                </p>
              </CardContent>
            </Card>

            {/* Information We Collect */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Information We Collect</CardTitle>
                <CardDescription>What data we collect and why</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Account Information</h3>
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    <li>Email address (for account identification and communication)</li>
                    <li>Username (for account identification)</li>
                    <li>Full name (optional, for personalization)</li>
                    <li>Password (encrypted and hashed, never stored in plain text)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Billing Information</h3>
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    <li>Payment information is processed by Stripe, our payment processor</li>
                    <li>We store your Stripe Customer ID and Subscription ID</li>
                    <li>We do not store credit card numbers or payment details directly</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Scan Data</h3>
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    <li>URLs of websites you scan</li>
                    <li>Security scan results and vulnerability findings</li>
                    <li>Scan configurations and preferences</li>
                    <li>API endpoints discovered during scans</li>
                    <li>Security scores and compliance metrics</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Usage Data</h3>
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    <li>Login timestamps and activity logs</li>
                    <li>IP addresses for security and fraud prevention</li>
                    <li>Browser type and user agent</li>
                    <li>Pages visited and features used</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Branding Customization (Optional)</h3>
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    <li>Company name and contact information</li>
                    <li>Logo and branding assets you upload</li>
                    <li>Color scheme preferences</li>
                    <li>Custom messaging for reports</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Your Information */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">How We Use Your Information</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Provide and maintain our security scanning service</li>
                  <li>Process your transactions and manage subscriptions</li>
                  <li>Send you security scan results and notifications</li>
                  <li>Improve and optimize our scanning tools and platform</li>
                  <li>Detect and prevent fraud and abuse</li>
                  <li>Communicate with you about service updates, security alerts, and support</li>
                  <li>Generate white-labeled security reports with your branding</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Data Retention</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>We retain your information for as long as necessary to provide our services and comply with legal obligations:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li><strong>Account data:</strong> Retained while your account is active and for 30 days after deletion</li>
                  <li><strong>Scan results:</strong> Retained for the duration of your subscription plus 90 days</li>
                  <li><strong>Billing records:</strong> Retained for 7 years for tax and accounting purposes</li>
                  <li><strong>Audit logs:</strong> Retained for 1 year for security and compliance</li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Data Sharing and Disclosure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>We do not sell, trade, or rent your personal information. We may share your information only in the following circumstances:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li><strong>Service Providers:</strong> Stripe for payment processing (subject to their privacy policy)</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your data</li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Data Security</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>We implement industry-standard security measures to protect your data:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>All data transmitted is encrypted using TLS/SSL</li>
                  <li>Passwords are hashed using bcrypt</li>
                  <li>Sensitive credentials are encrypted at rest</li>
                  <li>HTTP-only, secure cookies for authentication</li>
                  <li>CSRF protection on all state-changing operations</li>
                  <li>Rate limiting to prevent abuse</li>
                  <li>Regular security audits and updates</li>
                </ul>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Your Rights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>You have the following rights regarding your personal data:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                  <li><strong>Export:</strong> Download your scan data and results</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing emails (you'll still receive service-related communications)</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, please contact us at the email address below.
                </p>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Cookies and Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>We use cookies and similar tracking technologies to:</p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Keep you logged in (authentication cookies)</li>
                  <li>Remember your preferences</li>
                  <li>Protect against CSRF attacks</li>
                  <li>Analyze usage patterns to improve our service</li>
                </ul>
                <p className="mt-4">
                  You can control cookies through your browser settings, but disabling certain cookies may limit functionality.
                </p>
              </CardContent>
            </Card>

            {/* Children's Privacy */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Children's Privacy</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Our service is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </CardContent>
            </Card>

            {/* International Users */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">International Data Transfers</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Your data may be transferred to and processed in the United States or other countries where our service providers operate. By using our service, you consent to such transfers. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                </p>
              </CardContent>
            </Card>

            {/* Changes to Policy */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Changes to This Privacy Policy</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.
                </p>
              </CardContent>
            </Card>

            {/* Contact Us */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Contact Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="font-semibold">Fusegu Security</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Email: <a href="mailto:support@fusegusecurity.com" className="text-primary hover:underline">support@fusegusecurity.com</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer Navigation */}
          <div className="mt-12 pt-8 border-t text-center space-y-4">
            <div className="flex justify-center gap-6 text-sm">
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>
              <Link to="/" className="text-primary hover:underline">
                Home
              </Link>
              <Link to="/login" className="text-primary hover:underline">
                Sign In
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">
              © 2025 Fusegu Security. All rights reserved.
            </p>
          </div>
        </div>
      </main>
      </div>
    </>
  )
}
